import { useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonList, IonItem, IonLabel, IonButton, IonIcon, IonSpinner, IonChip,
} from '@ionic/react';
import { addOutline, refreshOutline, trashOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAccountsStore } from '@/store/data.store';
import StatusPill from '@/components/StatusPill';
import PlatformChip from '@/components/PlatformChip';
import EmptyState from '@/components/EmptyState';
import { linkOutline } from 'ionicons/icons';

export default function AccountsPage() {
  const { t } = useTranslation();
  const accounts = useAccountsStore((s) => s.accounts);
  const platforms = useAccountsStore((s) => s.platforms);
  const loading = useAccountsStore((s) => s.loading);
  const load = useAccountsStore((s) => s.load);
  const connect = useAccountsStore((s) => s.connect);
  const sync = useAccountsStore((s) => s.sync);
  const disconnect = useAccountsStore((s) => s.disconnect);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = new Set(accounts.map((account) => account.platform_id));
  const available = platforms.filter((platform) => !connected.has(platform.id));

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('accounts.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading && accounts.length === 0 ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : (
          <div className="page-shell page-shell--flush">
            {accounts.length === 0 ? (
              <EmptyState icon={linkOutline} title={t('accounts.noAccounts')} body={t('accounts.connectFirst')} />
            ) : (
              <IonList inset>
                {accounts.map((account) => (
                  <IonItem key={account.id}>
                    <div slot="start"><PlatformChip platform={account.platform_id} /></div>
                    <IonLabel>
                      <h3>{account.account_name}</h3>
                      <p>
                        {new Intl.NumberFormat('en').format(account.followers)} followers ·{' '}
                        {account.last_synced_at ? new Date(account.last_synced_at).toLocaleDateString() : t('accounts.syncManual')}
                      </p>
                    </IonLabel>
                    <div slot="end" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <StatusPill status={account.status} />
                      <IonButton size="small" fill="clear" onClick={() => void sync(account.id)}>
                        <IonIcon slot="icon-only" icon={refreshOutline} />
                      </IonButton>
                      <IonButton size="small" fill="clear" color="danger" onClick={() => void disconnect(account.id)}>
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    </div>
                  </IonItem>
                ))}
              </IonList>
            )}

            <h6 className="section-title">{t('accounts.platforms')}</h6>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 14px' }}>
              {available.map((platform) => (
                <IonChip key={platform.id} outline={!platform.configured} color={platform.configured ? 'primary' : 'medium'}
                  onClick={() => void connect(platform.id)}>
                  <IonIcon icon={addOutline} />
                  <span>{t(`platforms.${platform.id}`)}</span>
                  {!platform.configured && <small style={{ marginLeft: 4 }}>({t('accounts.notConfigured')})</small>}
                </IonChip>
              ))}
            </div>
            {platforms.some((platform) => platform.requiresReview) && (
              <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', padding: '10px 16px' }}>
                {t('accounts.reviewRequired')}
              </p>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}
